import { Activity, Box, Cpu, Database, Globe, Users } from "lucide-react";
import { Tooltip } from "../components/ui/Tooltip";
import { StatusIndicator } from "../components/ui/StatusIndicator";
import { usePublicNetworkTelemetry } from "../features/network/networkTelemetryHooks";

const SERVICES = [
  { label: "Woven Runtime", icon: Globe, status: "Online", tone: "ok" },
  { label: "Session Relay", icon: Users, status: "Online", tone: "ok" },
  { label: "Spatial Subspaces", icon: Box, status: "Online", tone: "ok" },
  { label: "Persistent State", icon: Database, status: "Preview", tone: "info" },
  { label: "Inference Gateway", icon: Cpu, status: "Preview", tone: "info" },
] as const;

const formatCount = (value: number | null | undefined) =>
  value == null ? "—" : value.toLocaleString("en-US");

const formatTimestamp = (value: string | null | undefined) =>
  value
    ? new Date(value).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
        timeZone: "UTC",
        timeZoneName: "short",
      })
    : "—";

export function NetworkPage() {
  const { data, loading, error, pollingStopped, reload } = usePublicNetworkTelemetry();
  const initialLoading = loading && !data;
  const formatMetric = (value: number | null | undefined) =>
    initialLoading ? "Loading…" : formatCount(value);
  const observed = data?.freshness !== "unavailable" ? data : null;
  const connected = formatMetric(observed?.connectionsActive);
  const tracking = data?.lifetimeTracking;
  const durable = tracking?.scope === "durable";
  const lifetime = durable ? data?.lifetime : observed?.lifetime;
  const metrics = [
    { label: "Regions", value: "1", icon: Globe, tone: "var(--accent-blue)" },
    { label: "Connected now", value: connected, icon: Users, tone: "var(--accent-cyan)" },
    {
      label: durable ? "Events · lifetime" : "Events · node run",
      value: formatMetric(lifetime?.eventsDelivered),
      icon: Activity,
      tone: "var(--accent-violet)",
    },
    {
      label: durable ? "Publishes · lifetime" : "Publishes · node run",
      value: formatMetric(lifetime?.publishes),
      icon: Database,
      tone: "var(--accent-magenta)",
    },
  ];
  const counters = [
    ["Connections accepted", lifetime?.connections],
    ["Publishes processed", lifetime?.publishes],
    ["Event delivery attempts", lifetime?.eventsDelivered],
    ["Payload bytes received", lifetime?.bytesReceived],
    ["Payload bytes fanned out", lifetime?.bytesDelivered],
    ["Queue capacity drops", lifetime?.queueDropped],
    ["Queue evictions / coalesces", lifetime?.queueEvicted],
  ] as const;

  return (
    <div className="page page--tight">
      <div className="status-page-head">
        <div>
          <h1 className="page-title">
            Our Network
            <span className="badge" role="status">
              {loading
                ? initialLoading
                  ? "Loading metrics…"
                  : "Refreshing metrics…"
                : `Telemetry ${data?.freshness ?? "unavailable"}`}
            </span>
          </h1>
          <p className="page-lede">
            Live observations and recorded activity from one configured Woven node in US Central,
            not a federated global network.
          </p>
        </div>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={reload}
          disabled={loading}
        >
          {error || data?.freshness === "unavailable" ? "Retry metrics" : "Refresh metrics"}
        </button>
      </div>

      <p className="status-activity-sub">
        {!loading && (error || data?.freshness === "unavailable")
          ? pollingStopped
            ? "Metrics could not be loaded. Automatic retries stopped. Select Retry metrics to try again."
            : "Metrics are unavailable. Retrying automatically while this tab is visible and online; you can also retry now."
          : "Metrics refresh every 20 seconds while this tab is visible and online."}
      </p>

      <div className="metric-grid" aria-busy={loading}>
        {metrics.map((metric) => (
          <div className="metric-tile" key={metric.label}>
            <div className="metric-tile-head">
              <metric.icon
                size={18}
                strokeWidth={1.75}
                absoluteStrokeWidth
                aria-hidden="true"
                style={{ color: metric.tone }}
              />
              <p className="metric-label">
                {metric.label}
                {metric.label === "Connected now" && (
                  <>
                    {" "}
                    <Tooltip label="About connected users">
                      Current occupancy across this configured node, not account capacity or an
                      audience estimate. Stale values are the last observation.
                    </Tooltip>
                  </>
                )}
              </p>
            </div>
            <p className="metric-value" title={metric.value === "—" ? "Unavailable" : undefined}>
              {metric.value}
            </p>
          </div>
        ))}
      </div>

      <div className="status-columns">
        <section className="panel status-activity" aria-busy={loading}>
          <div className="activity-head">
            <div>
              <h2 className="panel-title">Network activity</h2>
              <p className="status-activity-sub">
                {initialLoading
                  ? "Fetching the latest observations and recorded activity."
                  : durable
                    ? "Observed lifetime totals retained across node restarts and deploys."
                    : "Current node-run totals. Durable tracking is unavailable; counters reset on node restart."}
              </p>
            </div>
            <div className="activity-readout">
              <span className="activity-readout-value">{connected}</span>
              <span className="activity-readout-label">
                {data?.freshness === "stale" ? "last observed connected" : "connected"}
              </span>
            </div>
          </div>
          <dl className="status-service-list">
            {counters.map(([label, value]) => (
              <div className="status-service-row" key={label}>
                <dt>{label}</dt>
                <dd className="cell-mono">{formatMetric(value)}</dd>
              </div>
            ))}
          </dl>
          {durable && (
            <dl className="status-activity-timestamps">
              <div>
                <dt>Tracking enabled</dt>
                <dd>
                  <time dateTime={tracking?.trackedSince ?? undefined}>
                    {formatTimestamp(tracking?.trackedSince)}
                  </time>
                </dd>
              </div>
              <div>
                <dt>Last saved observation</dt>
                <dd>
                  <time dateTime={tracking?.observedAt}>
                    {formatTimestamp(tracking?.observedAt)}
                  </time>
                </dd>
              </div>
            </dl>
          )}
        </section>

        <section className="panel status-services">
          <h2 className="panel-title">Services</h2>
          <p className="status-activity-sub">
            Capability availability, not live health checks. Persistence beyond Ephemeral,
            inference, and Pro and Dedicated offerings remain Preview.
          </p>
          <ul className="status-service-list">
            {SERVICES.map((service) => (
              <li key={service.label} className="status-service-row">
                <span className="status-service-name">
                  <service.icon
                    size={16}
                    strokeWidth={1.75}
                    absoluteStrokeWidth
                    aria-hidden="true"
                  />
                  {service.label}
                </span>
                <StatusIndicator tone={service.tone} label={service.status} />
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="panel">
        <h2 className="panel-title">Regions</h2>
        <div className="region-grid">
          <div className="region-card">
            <StatusIndicator tone="info" label="US Central · Iowa" />
            <div className="region-card-row">
              <span className="region-latency">us-central1</span>
              <span className="region-health">1 configured node</span>
            </div>
          </div>
        </div>
      </section>

      <p className="status-footer-stat">
        <span className="status-footer-stat-value">{formatMetric(lifetime?.eventsDelivered)}</span>{" "}
        events woven · {durable ? "observed lifetime" : "node run"} · recipient delivery attempts
      </p>
    </div>
  );
}
